import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { StoreAPI } from '@/services/store'
import { showLoadingToast, closeToast, showErrorToast } from '@/utils/toast'
import { HomeAPI } from '@/services/home'

// 充值商品类型
export interface RechargeItem {
  id: string
  amount: number
  price: number
  originalPrice?: number
  giftAmount?: number
  limitBuy?: number
  image?: string
  title?: string
  description?: string
}

// 充值渠道分组（新数据结构）
export interface RechargeChannelGroup {
  channel: number
  name: string
  icon?: { unSelected?: string; selected?: string }
  list: RechargeItem[]
  apiUrl?: string
}

// 消费记录类型
export interface ConsumptionRecord {
  id: string
  type: number
  createdAt: number
  price?: number
  timestamp: string
  description: string
  amount: number
  status: 'success' | 'pending' | 'failed'
}

// Store 页面状态类型
export interface StoreState {
  activeTab: number
  rechargeItems: RechargeChannelGroup[]
  consumptionRecords: ConsumptionRecord[]
  loading: boolean
  initialized: boolean
  hasMoreConsumption: boolean
  consumptionPage: number
  consumptionPageSize: number
  subscribePackages: any[]
  error: string | null // 🔧 新增：错误信息
}

export const useStorePageStore = defineStore('storePage', () => {
  // 状态
  const activeTab = ref<number>(0)
  const rechargeItems = ref<RechargeChannelGroup[]>([])
  const consumptionRecords = ref<ConsumptionRecord[]>([])
  const loading = ref<boolean>(false)
  const initialized = ref<boolean>(false)
  const hasMoreConsumption = ref<boolean>(true)
  const consumptionPage = ref<number>(1)
  const consumptionPageSize = ref<number>(20)
  const subscribePackages = ref<any[]>([])
  const error = ref<string | null>(null) // 🔧 新增：错误信息

  // 图片映射
  const storeImages = [
    () => import('@/assets/images/store/store-1.webp'),
    () => import('@/assets/images/store/store-2.webp'),
    () => import('@/assets/images/store/store-3.webp'),
    () => import('@/assets/images/store/store-4.webp'),
    () => import('@/assets/images/store/store-5.webp'),
    () => import('@/assets/images/store/store-6.webp'),
    () => import('@/assets/images/store/store-7.webp')
  ]

  // 计算属性
  const isRechargeTab = computed(() => activeTab.value === 0)
  const isConsumptionTab = computed(() => activeTab.value === 1)
  const hasRechargeItems = computed(() => rechargeItems.value.length > 0)
  const hasConsumptionRecords = computed(() => consumptionRecords.value.length > 0)

  /**
   * 为API数据分配图片
   */
  const mapImagesToItems = async (channels: any[]): Promise<RechargeChannelGroup[]> => {
    const mappedChannels: RechargeChannelGroup[] = []

    for (let c = 0; c < channels.length; c++) {
      const channel = channels[c] || {}
      const list = Array.isArray(channel.list) ? channel.list : []

      const mappedList: RechargeItem[] = []
      for (let i = 0; i < list.length; i++) {
        const pkg = list[i]
        let imageIndex = i
        if (imageIndex >= storeImages.length) imageIndex = storeImages.length - 1

        try {
          const imageModule = await storeImages[imageIndex]()
          mappedList.push({
            ...pkg,
            image: (imageModule as any).default
          })
        } catch (error) {
          console.error('Failed to load store image:', error)
          const fallbackModule = await storeImages[0]()
          mappedList.push({
            ...pkg,
            image: (fallbackModule as any).default
          })
        }
      }

      mappedChannels.push({
        channel: Number(channel.channel) || 0,
        name: channel.name || '',
        icon: channel.icon,
        apiUrl: channel.apiUrl,
        list: mappedList
      })
    }

    return mappedChannels
  }

  /**
   * 获取充值商品列表
   */
  const fetchRechargeItems = async (showLoading = true): Promise<void> => {
    try {
      if (showLoading) {
        // showLoadingToast()
        loading.value = true
      }
      error.value = null // 🔧 清除之前的错误

      const res = await StoreAPI.getRechargePackageChannel({
        limitBuy: 0
      })

      if (res.data && res.data.length > 0) {
        rechargeItems.value = await mapImagesToItems(res.data)
        initialized.value = true // 🔧 标记已初始化
      } else {
        rechargeItems.value = []
      }
    } catch (err: any) {
      console.error('Failed to fetch recharge items:', err)
      error.value = err?.message || 'Failed to load recharge packages' // 🔧 保存错误信息
      rechargeItems.value = []
    } finally {
      if (showLoading) {
        // closeToast()
        loading.value = false
      }
    }
  }

  /**
   * 获取消费记录
   */
  const fetchConsumptionRecords = async (page = 1, showToast = true): Promise<void> => {
    try {
      if (showToast && page === 1) {
        // showLoadingToast()
      }
      loading.value = true
      if (page === 1) {
        error.value = null // 🔧 清除之前的错误（仅在首次加载时）
      }

      const res = await StoreAPI.getConsumptionRecord({
        page,
        pageSize: consumptionPageSize.value
      })
      console.log('🚀 ~ fetchConsumptionRecords ~ res:', res)

      if (page === 1) {
        consumptionRecords.value = res.data || []
      } else {
        consumptionRecords.value.push(...(res.data || []))
      }

      consumptionPage.value = page
      // 如果返回的数据少于 pageSize，说明没有更多数据了
      hasMoreConsumption.value = res.data && res.data.length >= consumptionPageSize.value
      console.log('hasMoreConsumption:', hasMoreConsumption.value, 'dataLength:', res.data?.length)
    } catch (err: any) {
      console.error('Failed to fetch consumption records:', err)
      error.value = err?.message || 'Failed to load consumption records' // 🔧 保存错误信息
      hasMoreConsumption.value = false
    } finally {
      if (showToast && page === 1) {
        closeToast()
      }
      loading.value = false
    }
  }

  /**
   * 加载更多消费记录
   */
  const loadMoreConsumption = async (): Promise<void> => {
    if (!hasMoreConsumption.value || loading.value) return

    await fetchConsumptionRecords(consumptionPage.value + 1, false)
  }

  /**
   * 刷新当前tab数据
   */
  const refreshCurrentTab = async (): Promise<void> => {
    if (isRechargeTab.value) {
      await fetchRechargeItems()
    } else {
      consumptionPage.value = 1
      await fetchConsumptionRecords(1)
    }
  }

  /**
   * 切换tab
   */
  const switchTab = async (tabIndex: number): Promise<void> => {
    activeTab.value = tabIndex

    // 如果切换到消费记录tab且还没有数据，则加载数据
    if (tabIndex === 1) {
      await fetchConsumptionRecords()
    }else{
      await fetchRechargeItems()
    }
  }

  /**
   * 初始化store数据
   */
  const initialize = async (): Promise<void> => {
    if (initialized.value) return

    try {
      await fetchRechargeItems()
      initialized.value = true
    } catch (error) {
      console.error('Failed to initialize store data:', error)
    }
  }

  /**
   * 重置store状态
   */
  const reset = (): void => {
    activeTab.value = 0
    rechargeItems.value = []
    consumptionRecords.value = []
    loading.value = false
    initialized.value = false
    hasMoreConsumption.value = true
    consumptionPage.value = 1
  }

  /**
   * 计算折扣率
   */
  const calculateDiscountRate = (price: number, originalPrice: number): number => {
    if (!originalPrice || originalPrice <= price) return 0
    return Math.round(((originalPrice - price) / originalPrice) * 100)
  }

  /**
   * 处理充值商品点击
   */
  const handleRechargeItemClick = async (item: RechargeItem): Promise<void> => {
    try {
      showLoadingToast('Processing...')

      // TODO: 实现充值逻辑
      console.log('Recharge item clicked:', item)

      // 这里应该调用支付API
      // await StoreAPI.createPayment(item.id)
    } catch (error) {
      console.error('Failed to process recharge:', error)
      showErrorToast('Payment failed, please try again')
    } finally {
      closeToast()
    }
  }
  /**
   * 获取svip套餐
   */
  const getSubscribePackages = async (): Promise<any> => {
    const res = await HomeAPI.getSubscribePackages({})
    if (res.data && res.data.length > 0) {
      subscribePackages.value = res.data
    } else {
      subscribePackages.value = []
    }
  }

  return {
    // 状态
    activeTab,
    rechargeItems,
    consumptionRecords,
    loading,
    initialized,
    hasMoreConsumption,
    consumptionPage,
    consumptionPageSize,
    subscribePackages,
    error, // 🔧 新增：错误信息

    // 计算属性
    isRechargeTab,
    isConsumptionTab,
    hasRechargeItems,
    hasConsumptionRecords,
    storeImages,

    // 方法
    fetchRechargeItems,
    fetchConsumptionRecords,
    loadMoreConsumption,
    refreshCurrentTab,
    switchTab,
    initialize,
    reset,
    calculateDiscountRate,
    handleRechargeItemClick,
    getSubscribePackages,
  }
})
